/**
 * Live Google Cloud pricing for a stack (ENG-443): the size presets, the
 * usage assumptions, and the pure maths that turn catalog unit prices into
 * a per-item monthly range.
 *
 * Holds NO prices. Every unit price is passed in, read from the Cloud Billing
 * Catalog API by the API's gcpPricingService. (stack-cost.ts is the older,
 * dated snapshot kept for the stack card; it is superseded by this.)
 */
export const GCP_SIZES = ["starter", "team", "production"];
/** "supabase": the database is Supabase's, not Google's, so it costs $0 here. */
export const GCP_DATABASES = ["none", "supabase", "cloudsql"];
/** Catalog service display names, in the order they are priced. */
export const GCP_PRICED_SERVICES = [
    "Cloud Run",
    "Cloud SQL",
    "Artifact Registry",
    "Secret Manager",
    "Cloud Storage",
    "Cloud Logging",
];
/** Which catalog service each price comes from. */
export const GCP_PRICE_SERVICE = {
    cloudRunVcpuSecond: "Cloud Run",
    cloudRunGibSecond: "Cloud Run",
    cloudRunRequest: "Cloud Run",
    sqlVcpuHour: "Cloud SQL",
    sqlRamGibHour: "Cloud SQL",
    sqlStorageGibMonth: "Cloud SQL",
    sqlHaVcpuHour: "Cloud SQL",
    sqlHaRamGibHour: "Cloud SQL",
    sqlHaStorageGibMonth: "Cloud SQL",
    sqlBackupGibMonth: "Cloud SQL",
    artifactStorageGibMonth: "Artifact Registry",
    secretVersionMonth: "Secret Manager",
    secretAccessOperation: "Secret Manager",
    storageGibMonth: "Cloud Storage",
    logGib: "Cloud Logging",
};
/** What each running Cloud Run instance is given (Cloud Run's defaults). */
export const GCP_INSTANCE = { vcpu: 1, gib: 0.5 };
export const GCP_MONTH_HOURS = 730;
const MONTH_SECONDS = GCP_MONTH_HOURS * 3600;
const small = { vcpu: 1, ramGib: 3.75, storageGb: 10, highAvailability: false };
export const GCP_SIZE_PRESETS = {
    starter: {
        label: "Starter",
        environments: [{ environment: "dev", maxInstances: 2, db: small }],
        usage: {
            lowRequests: 10_000,
            lowRequestSeconds: 0.2,
            highBusyShare: 0.1,
            highRequestsPerSecond: 10,
            imagesGb: { low: 0.5, high: 2 },
            storageGb: { low: 0.01, high: 1 },
            secretsPerEnv: 2,
            secretAccessesPerEnv: { low: 1_000, high: 100_000 },
            logsGibPerEnv: { low: 0.1, high: 5 },
            backupFactor: { low: 0.5, high: 2 },
        },
    },
    team: {
        label: "Team",
        environments: [
            { environment: "dev", maxInstances: 2, db: small },
            { environment: "stage", maxInstances: 2, db: small },
            { environment: "prod", maxInstances: 10, db: { vcpu: 2, ramGib: 7.5, storageGb: 20, highAvailability: false } },
        ],
        usage: {
            lowRequests: 10_000,
            lowRequestSeconds: 0.2,
            highBusyShare: 0.25,
            highRequestsPerSecond: 20,
            imagesGb: { low: 0.5, high: 2 },
            storageGb: { low: 0.01, high: 5 },
            secretsPerEnv: 3,
            secretAccessesPerEnv: { low: 1_000, high: 500_000 },
            logsGibPerEnv: { low: 0.1, high: 20 },
            backupFactor: { low: 0.5, high: 2 },
        },
    },
    production: {
        label: "Production",
        environments: [
            { environment: "dev", maxInstances: 2, db: small },
            { environment: "stage", maxInstances: 5, db: { vcpu: 2, ramGib: 7.5, storageGb: 20, highAvailability: false } },
            { environment: "prod", maxInstances: 20, db: { vcpu: 4, ramGib: 15, storageGb: 100, highAvailability: true } },
        ],
        usage: {
            lowRequests: 100_000,
            lowRequestSeconds: 0.2,
            highBusyShare: 0.5,
            highRequestsPerSecond: 50,
            imagesGb: { low: 1, high: 5 },
            storageGb: { low: 0.1, high: 50 },
            secretsPerEnv: 5,
            secretAccessesPerEnv: { low: 10_000, high: 2_000_000 },
            logsGibPerEnv: { low: 1, high: 100 },
            backupFactor: { low: 1, high: 3 },
        },
    },
};
/** The catalog prices needed to price a stack with this database choice. */
export function requiredGcpPriceKeys(database, size) {
    const keys = [
        "cloudRunVcpuSecond",
        "cloudRunGibSecond",
        "cloudRunRequest",
        "artifactStorageGibMonth",
        "secretVersionMonth",
        "secretAccessOperation",
        "storageGibMonth",
        "logGib",
    ];
    if (database === "cloudsql") {
        const envs = GCP_SIZE_PRESETS[size].environments;
        keys.push("sqlBackupGibMonth");
        if (envs.some((e) => !e.db.highAvailability))
            keys.push("sqlVcpuHour", "sqlRamGibHour", "sqlStorageGibMonth");
        if (envs.some((e) => e.db.highAvailability))
            keys.push("sqlHaVcpuHour", "sqlHaRamGibHour", "sqlHaStorageGibMonth");
    }
    return keys;
}
/** Cost of `quantity` units against a tiered price (the catalog's own free tiers are tiers priced at 0). */
export function tieredCost(tiers, quantity) {
    const sorted = [...tiers].sort((a, b) => a.start - b.start);
    let total = 0;
    sorted.forEach((tier, i) => {
        const end = sorted[i + 1]?.start ?? Infinity;
        const used = Math.min(quantity, end) - tier.start;
        if (used > 0)
            total += used * tier.unitPrice;
    });
    return total;
}
function price(prices, key) {
    const tiers = prices[key];
    if (!tiers || tiers.length === 0)
        throw new Error(`Missing catalog price: ${key}`);
    return tiers;
}
const round2 = (n) => Math.round(n * 100) / 100;
const fmt = (n) => n.toLocaleString("en-US");
/**
 * Prices a stack from catalog unit prices. Usage is summed across the
 * environments first, so a tiered price (including the free tier, which is
 * per billing account) is applied once to the whole stack.
 */
export function estimateGcpStack(prices, input) {
    const { database, size } = input;
    const preset = GCP_SIZE_PRESETS[size];
    const u = preset.usage;
    const envs = preset.environments;
    const n = envs.length;
    const sum = (f) => envs.reduce((t, e) => t + f(e), 0);
    const range = (low, high) => ({ low: round2(low), high: round2(high) });
    // Cloud Run
    const lowBusy = n * u.lowRequests * u.lowRequestSeconds;
    const highBusy = sum((e) => e.maxInstances) * MONTH_SECONDS * u.highBusyShare;
    const highRequests = highBusy * u.highRequestsPerSecond;
    const runCost = (busy, requests) => tieredCost(price(prices, "cloudRunVcpuSecond"), busy * GCP_INSTANCE.vcpu) +
        tieredCost(price(prices, "cloudRunGibSecond"), busy * GCP_INSTANCE.gib) +
        tieredCost(price(prices, "cloudRunRequest"), requests);
    const items = [
        {
            id: "cloudRun",
            label: "Cloud Run",
            ...range(runCost(lowBusy, n * u.lowRequests), runCost(highBusy, highRequests)),
            assumptions: [
                `Each instance has ${String(GCP_INSTANCE.vcpu)} vCPU and ${String(GCP_INSTANCE.gib * 1024)} MiB and is billed only while handling requests.`,
                `Low: ${fmt(u.lowRequests)} requests a month per environment, ${String(u.lowRequestSeconds * 1000)} ms each.`,
                `High: ${envs.map((e) => `${e.environment} ${String(e.maxInstances)}`).join(", ")} instances busy ${String(u.highBusyShare * 100)}% of the month at ${String(u.highRequestsPerSecond)} requests a second each.`,
                "Free usage in the catalog is counted once for the whole stack.",
            ],
        },
    ];
    // Database and its backups
    if (database === "cloudsql") {
        const dbCost = envs.reduce((t, e) => {
            const ha = e.db.highAvailability;
            return (t +
                tieredCost(price(prices, ha ? "sqlHaVcpuHour" : "sqlVcpuHour"), e.db.vcpu * GCP_MONTH_HOURS) +
                tieredCost(price(prices, ha ? "sqlHaRamGibHour" : "sqlRamGibHour"), e.db.ramGib * GCP_MONTH_HOURS) +
                tieredCost(price(prices, ha ? "sqlHaStorageGibMonth" : "sqlStorageGibMonth"), e.db.storageGb));
        }, 0);
        const backupGb = (factor) => sum((e) => e.db.storageGb) * factor;
        const cost = round2(dbCost);
        items.push({
            id: "database",
            label: "Cloud SQL (PostgreSQL)",
            low: cost,
            high: cost,
            assumptions: [
                `One always-on PostgreSQL instance per environment, ${String(GCP_MONTH_HOURS)} hours a month: ${envs
                    .map((e) => `${e.environment} ${String(e.db.vcpu)} vCPU / ${String(e.db.ramGib)} GiB / ${String(e.db.storageGb)} GB${e.db.highAvailability ? " high availability" : ""}`)
                    .join("; ")}.`,
                "The database is billed whether or not the app has traffic, so low and high match.",
            ],
        }, {
            id: "backups",
            label: "Database backups",
            ...range(tieredCost(price(prices, "sqlBackupGibMonth"), backupGb(u.backupFactor.low)), tieredCost(price(prices, "sqlBackupGibMonth"), backupGb(u.backupFactor.high))),
            assumptions: [`Backups take ${String(u.backupFactor.low)}–${String(u.backupFactor.high)}× the provisioned database storage.`],
        });
    }
    else {
        const why = database === "supabase"
            ? "Supabase hosts the database and bills it separately, so Google Cloud charges nothing for it."
            : "No database is part of the stack.";
        items.push({ id: "database", label: database === "supabase" ? "Supabase" : "Database", low: 0, high: 0, assumptions: [why] }, { id: "backups", label: "Database backups", low: 0, high: 0, assumptions: [why] });
    }
    const perEnv = (r) => ({ low: r.low * n, high: r.high * n });
    const images = perEnv(u.imagesGb);
    const files = perEnv(u.storageGb);
    const logs = perEnv(u.logsGibPerEnv);
    const accesses = perEnv(u.secretAccessesPerEnv);
    const secrets = u.secretsPerEnv * n;
    const secretCost = (access) => tieredCost(price(prices, "secretVersionMonth"), secrets) + tieredCost(price(prices, "secretAccessOperation"), access);
    items.push({
        id: "storage",
        label: "Cloud Storage",
        ...range(tieredCost(price(prices, "storageGibMonth"), files.low), tieredCost(price(prices, "storageGibMonth"), files.high)),
        assumptions: [`${String(u.storageGb.low)}–${String(u.storageGb.high)} GB per environment (Terraform state and app files).`],
    }, {
        id: "images",
        label: "Container images",
        ...range(tieredCost(price(prices, "artifactStorageGibMonth"), images.low), tieredCost(price(prices, "artifactStorageGibMonth"), images.high)),
        assumptions: [`${String(u.imagesGb.low)}–${String(u.imagesGb.high)} GB of stored images per environment in Artifact Registry.`],
    }, {
        id: "secrets",
        label: "Secrets",
        ...range(secretCost(accesses.low), secretCost(accesses.high)),
        assumptions: [
            `${String(u.secretsPerEnv)} active secret versions per environment.`,
            `${fmt(u.secretAccessesPerEnv.low)}–${fmt(u.secretAccessesPerEnv.high)} accesses a month per environment.`,
        ],
    }, {
        id: "logs",
        label: "Logs",
        ...range(tieredCost(price(prices, "logGib"), logs.low), tieredCost(price(prices, "logGib"), logs.high)),
        assumptions: [`${String(u.logsGibPerEnv.low)}–${String(u.logsGibPerEnv.high)} GiB of logs per environment each month.`],
    });
    return {
        provider: "gcp",
        region: input.region,
        database,
        size,
        sizeLabel: preset.label,
        currency: "USD",
        asOf: input.asOf,
        source: "cloud-billing-catalog",
        items,
        total: range(items.reduce((t, i) => t + i.low, 0), items.reduce((t, i) => t + i.high, 0)),
        assumptions: [
            `Unit prices are Google's list prices for ${input.region}, read from the Cloud Billing Catalog API as of ${input.asOf}.`,
            `${preset.label} size: ${envs.map((e) => e.environment).join(", ")}.`,
            "Networking, egress, taxes, committed-use and other discounts, and services you add are not included.",
        ],
    };
}
//# sourceMappingURL=gcp-pricing.js.map